import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, BarChart3, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useProfile } from '@/api/profile'
import { useTeamPerformance } from '@/api/teamPerformance'
import { VoiceProvider } from '@/contexts/VoiceProvider'
import { useSSEHandlers } from '@/hooks/useSSEHandlers'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { AuthBootProvider } from '@/providers/AuthBootProvider'
import { ManagerBootProvider } from '@/providers/ManagerBootProvider'
import { GlobalCallModal } from '@/components/dashboard/GlobalCallModal'
import { InCallWidget } from '@/components/dashboard/InCallWidget'
import { InactiveTabOverlay } from '@/components/dashboard/InactiveTabOverlay'
import { AgentDrillContent } from '@/components/manager/analytics/AgentDrillContent'
import type { TeamPerformancePeriod } from '@/types/teamPerformance'

export function AgentAnalyticsPage() {
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const { data: profile, isLoading: profileLoading } = useProfile()
  useSSEHandlers()

  // Supervisors follow the team too (analytics, live, history).
  if (!profileLoading && profile?.role === 'AGENT' && profile?.access?.role !== 'supervisor') {
    return <Navigate to={`/${locale}/dashboard`} replace />
  }

  return (
    <VoiceProvider>
      <AuthBootProvider>
        <ManagerBootProvider>
          <Content />
        </ManagerBootProvider>
      </AuthBootProvider>
      <GlobalCallModal />
      <InCallWidget />
      <InactiveTabOverlay />
    </VoiceProvider>
  )
}

function Content() {
  const { t } = useTranslation()
  const { locale = 'fr', userId } = useParams<{ locale: string; userId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: profile } = useProfile()

  const agentUserId = userId ? Number(userId) : null
  const period = (searchParams.get('period') as TeamPerformancePeriod) || 'cette_semaine'

  // On charge team_performance pour récupérer l'agent ciblé + les team_totals
  // utiles à la comparaison. Si on a un cache valide, c'est instantané.
  const { data, isLoading, error } = useTeamPerformance(profile?.company, {
    period,
  })

  const agent =
    data && agentUserId
      ? data.agents.find((a) => a.user_id === agentUserId) ?? null
      : null

  useDocumentTitle(agent ? `${agent.name} · Analytics` : null)

  const pageBg = darkMode ? 'bg-slate-900' : 'bg-slate-50'
  const titleColor = darkMode ? 'text-white' : 'text-slate-900'
  const subtitleColor = darkMode ? 'text-slate-400' : 'text-slate-500'

  const goBackToAnalytics = () => {
    navigate(`/${locale}/manager?view=analytics&period=${period}`)
  }

  return (
    <div className={cn('h-screen flex flex-col', pageBg)}>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6 space-y-5 max-w-[1200px] mx-auto">
          {/* Breadcrumb + retour */}
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={goBackToAnalytics}
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md font-medium',
                'transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                darkMode
                  ? 'text-slate-300 hover:bg-slate-800'
                  : 'text-slate-700 hover:bg-slate-100',
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              {t('manager.analytics.drilldown.backToAnalytics')}
            </button>
            <ChevronRight
              className={cn('h-3.5 w-3.5', darkMode ? 'text-slate-600' : 'text-slate-400')}
            />
            <span className={cn('flex items-center gap-1.5', subtitleColor)}>
              <BarChart3 className="h-3.5 w-3.5" />
              {t('manager.tabs.analytics')}
            </span>
            <ChevronRight
              className={cn('h-3.5 w-3.5', darkMode ? 'text-slate-600' : 'text-slate-400')}
            />
            <span className={cn('font-medium', titleColor)}>
              {agent?.name ?? t('manager.analytics.agentPanel.loading')}
            </span>
          </div>

          {isLoading && (
            <div className="space-y-3">
              <div
                className={cn('h-16 rounded-xl animate-pulse', darkMode ? 'bg-slate-800' : 'bg-slate-200/70')}
              />
              <div
                className={cn('h-40 rounded-xl animate-pulse', darkMode ? 'bg-slate-800' : 'bg-slate-200/70')}
              />
              <div
                className={cn('h-64 rounded-xl animate-pulse', darkMode ? 'bg-slate-800' : 'bg-slate-200/70')}
              />
            </div>
          )}

          {error && !isLoading && (
            <div
              className={cn(
                'rounded-xl border p-6 text-center',
                darkMode ? 'border-rose-500/30 bg-rose-500/10' : 'border-rose-200 bg-rose-50',
              )}
            >
              <p className="text-rose-600 dark:text-rose-400 font-medium">
                {error instanceof Error ? error.message : t('manager.analytics.loadError')}
              </p>
            </div>
          )}

          {data && !isLoading && !agent && (
            <div
              className={cn(
                'rounded-xl border p-10 text-center',
                darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white',
              )}
            >
              <p className={cn('text-sm', subtitleColor)}>
                {t('manager.analytics.agentPanel.agentNotFound')}
              </p>
            </div>
          )}

          {data && agent && (
            <AgentDrillContent
              agent={agent}
              teamTotals={data.team_totals}
              period={period}
            />
          )}
        </div>
      </div>
    </div>
  )
}
